/**
 * Layout authoring panel — the WYSIWYG direct-manipulation editor on the
 * component config page.
 *
 * Renders one row per discovered {@link LayoutTarget} (field, column, section,
 * title, action) with inline affordances that each emit one of the engine's
 * layout {@link RuleEffect}s via {@link applyEdit}:
 *
 * - **Separate text inputs** — for fields/columns, stacked labelled
 *   `TextField`s: Label (`renameField`/`renameColumn`), Placeholder
 *   (`setFieldPlaceholder`, text-like fields only) and Helper text
 *   (`setFieldHelperText`, fields only). Each commits on blur / Enter; a blank
 *   value removes the override.
 * - **Visibility toggle** — a `Switch` reflecting the *effective* visibility
 *   (off when hidden). Toggling sets a desired visibility folded against the
 *   target's `baseHidden`: it emits `hideField`/`showField` (or the column
 *   pair) only when the desired state differs from the base, else removes the
 *   slot. A baseline-hidden target therefore stays listed (greyed) and can be
 *   toggled back on.
 * - **Tooltip editor + remove** — a `TextField` (`setFieldTooltip` /
 *   `setColumnTooltip`, blank soft-clears) plus an explicit "Remove tooltip"
 *   ghost button (`clearFieldTooltip` / `clearColumnTooltip`) shown when an
 *   effective tooltip exists, the only way to force a baseline tooltip off.
 * - **Condition editor** — a per-field sub-panel (`setFieldCondition`) staging
 *   "hide / disable / require THIS field while ANOTHER field of the same form
 *   holds value X" (plus, on a checkbox field, "tick / untick it when ANOTHER
 *   field starts holding value X"): a staged list with per-row removal plus an
 *   add form (source field / operator / value / effect). The only affordance
 *   whose predicate is re-evaluated at runtime, against the form's own values.
 * - **Title row** — a single label input (`setTitle`) when a title target
 *   exists.
 * - **Button rows** — per action target: a label input (`setActionLabel`) and
 *   a visibility `Switch` (`hideAction`).
 * - **Arrow reorder** — Up / Down icon buttons per row (fields, columns,
 *   sections, rows), disabled at the boundaries; each emits the existing
 *   `reorder*` effect with the new key order (the engine contract is unchanged
 *   from the prior drag mechanic, only the gesture differs).
 *
 * No new base components — only `TextField`, `Select`, `Switch`, `Button`,
 * `Icon`, `Text`, `Badge`, `HStack`, `VStack` from the catalog.
 *
 * The panel is **controlled**: it never holds the effect list itself. The
 * parent owns `effects` state and passes `onEdit`, so the same draft list
 * feeds both this panel and the live preview (true WYSIWYG round-trip).
 */

import { useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  GitBranch,
  MessageSquare,
  Trash2,
} from 'lucide-react';

import { Button } from '@components/base/actions';
import { Badge, Icon, Text } from '@components/base/display';
import { Select, Switch, TextField } from '@components/base/inputs';
import type { SelectOption } from '@components/base/inputs';
import { HStack, VStack } from '@components/base/layout';

import {
  effectiveActionLabel,
  effectiveConditions,
  effectiveHelperText,
  effectiveLabel,
  effectivePlaceholder,
  effectiveRequired,
  effectiveRowOrder,
  effectiveTargetOrder,
  effectiveTitle,
  effectiveTooltip,
  isActionHidden,
  isHidden,
  isSearchHidden,
  parseConditionValue,
  reorderByArrow,
  LIST_OPERATORS,
  VALUELESS_OPERATORS,
  type LayoutEdit,
  type LayoutTarget,
  type LayoutTargetKind,
  type RowTarget,
} from './layoutAuthoring';
import { targetsOfKind } from './discoverLayoutTargets';
import { SEARCH_TARGET_KEY, TITLE_TARGET_KEY } from './discoverConfigTargets';
import {
  DEFAULT_REQUIRED_MESSAGE,
  type RuleEffect,
} from '@components/shared/configurable';
import type {
  ConditionOperator,
  RuleFieldCondition,
} from '@components/integrated/entity-form';

// ---------------------------------------------------------------------------
// Row-reorder support model
// ---------------------------------------------------------------------------

/**
 * Whether the previewed surface supports manual row reordering, and why not
 * when it doesn't. Drives the row-reorder section: `{ supported: true }` shows
 * the reorderable rows; `{ supported: false, reason }` shows the documented
 * disabled message (the same constraint the engine enforces — manual order is
 * only sensible for a fully-loaded inline `data[]` with a stable row id).
 */
export type RowReorderSupport =
  | { readonly supported: true }
  | { readonly supported: false; readonly reason: string };

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LayoutAuthoringPanelProps {
  /** All discoverable targets across the live preview. */
  readonly targets: readonly LayoutTarget[];
  /** Current draft layout effects (owned by the parent). */
  readonly effects: readonly RuleEffect[];
  /**
   * Apply a single direct-manipulation edit. The parent folds it into its
   * effect state via {@link applyEdit} (re-exported so the parent doesn't
   * re-implement the reducer).
   */
  readonly onEdit: (edit: LayoutEdit) => void;
  /**
   * Data rows available for manual reorder (data-row-ordering Tier 2), in
   * their natural order. Empty / omitted when the surface is not a DataList or
   * exposes no rows. The panel previews the effective order by folding any
   * draft `reorderRows` effect with {@link effectiveRowOrder}.
   */
  readonly rowTargets?: readonly RowTarget[];
  /**
   * Whether the surface supports manual row reordering. When
   * `{ supported: false }` the row section renders the disabled `reason`
   * message instead of reorderable rows. Defaults to unsupported.
   */
  readonly rowReorderSupport?: RowReorderSupport;
}

// Group headings for every kind the panel renders.
const KIND_TITLES: Record<LayoutTargetKind, string> = {
  field: 'Fields',
  column: 'Columns',
  section: 'Sections',
  title: 'Title',
  action: 'Buttons',
  filter: 'Filtres',
  search: 'Recherche',
};

// ---------------------------------------------------------------------------
// Runtime field-condition authoring (`setFieldCondition`) — config constants
// ---------------------------------------------------------------------------

/**
 * Every user-facing string of the per-field Condition sub-panel, in one place
 * (configurable-ready authoring: labels live in config, never inline in JSX).
 */
const CONDITION_UI = {
  /** Ghost button that opens/closes the sub-panel. */
  toggle: 'Condition',
  /** Same button once conditions are staged — carries the count. */
  toggleStaged: (count: number) => `Condition ✓ (${String(count)})`,
  /** Heading above the staged-condition list. */
  stagedHeading: 'Conditions actives',
  /** Shown in place of the list while the field carries no condition. */
  stagedEmpty: 'Aucune condition sur ce champ.',
  /** Heading above the add form. */
  addHeading: 'Ajouter une condition',
  sourceLabel: 'Champ source',
  sourcePlaceholder: 'Choisir un champ',
  /** Shown instead of the add form when the field has no sibling to test. */
  noSource: "Aucun autre champ de ce composant ne peut servir de source.",
  operatorLabel: 'Opérateur',
  operatorPlaceholder: 'Choisir un opérateur',
  valueLabel: 'Valeur',
  valuePlaceholder: 'Valeur comparée',
  /** Helper under the value input for the list operators (`in` / `notIn`). */
  valueListHelper: 'Valeurs séparées par des virgules (ex. EXPERT, REPARATEUR)',
  effectLabel: 'Effet',
  effectPlaceholder: 'Choisir un effet',
  addButton: 'Ajouter la condition',
  /** Summary sentence parts: `Quand <source> <opérateur> "<valeur>" → <effet>`. */
  summaryPrefix: 'Quand',
  summaryArrow: '→',
  /** Fallback effect label for a loaded condition outside the presets. */
  customEffect: 'effet personnalisé',
} as const;

/**
 * The shared 11-operator vocabulary with its French authoring labels, in the
 * order the picker lists them. Source of truth for the type: `ConditionOperator`
 * in `src/components/integrated/shared/conditionEvaluator.ts`.
 */
const CONDITION_OPERATORS: ReadonlyArray<{
  readonly value: ConditionOperator;
  readonly label: string;
}> = [
  { value: 'equals', label: 'est égal à' },
  { value: 'notEquals', label: 'est différent de' },
  { value: 'contains', label: 'contient' },
  { value: 'notContains', label: 'ne contient pas' },
  { value: 'greaterThan', label: 'est supérieur à' },
  { value: 'lessThan', label: 'est inférieur à' },
  { value: 'isEmpty', label: 'est vide' },
  { value: 'isNotEmpty', label: "n'est pas vide" },
  { value: 'in', label: 'est parmi' },
  { value: 'notIn', label: "n'est pas parmi" },
  { value: 'matches', label: 'correspond à (regex)' },
];

/** `Select` options for the operator picker (mutable copy — `Select` needs one). */
const CONDITION_OPERATOR_OPTIONS: SelectOption[] = CONDITION_OPERATORS.map(
  (o) => ({ value: o.value, label: o.label }),
);

/**
 * The seven authorable `effect` payloads. `setFieldCondition.effect` accepts any
 * combination of `visible` / `disabled` / `required` / `checked`, but the editor
 * offers the single-dimension presets an admin actually reaches for: five state
 * presets for every field, plus "Cocher" / "Décocher" (`checked`, a one-shot
 * value write when the condition turns true) for checkbox targets only
 * (`checkboxOnly`, gated on {@link LayoutTarget.supportsCheckedCondition}). A
 * richer combination stays authorable through the raw JSON / NL routes and
 * still round-trips (it renders with {@link CONDITION_UI.customEffect}).
 */
const CONDITION_EFFECTS: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
  readonly effect: RuleFieldCondition['effect'];
  /** Offered only when the target field is a checkbox. */
  readonly checkboxOnly?: true;
}> = [
  { value: 'hide', label: 'Masquer', effect: { visible: false } },
  { value: 'show', label: 'Afficher', effect: { visible: true } },
  { value: 'disable', label: 'Désactiver (figer)', effect: { disabled: true } },
  { value: 'enable', label: 'Réactiver', effect: { disabled: false } },
  { value: 'require', label: 'Rendre obligatoire', effect: { required: true } },
  {
    value: 'check',
    label: 'Cocher',
    effect: { checked: true },
    checkboxOnly: true,
  },
  {
    value: 'uncheck',
    label: 'Décocher',
    effect: { checked: false },
    checkboxOnly: true,
  },
];

/** `Select` options for the effect picker on a non-checkbox target. */
const CONDITION_EFFECT_OPTIONS: SelectOption[] = CONDITION_EFFECTS.filter(
  (e) => e.checkboxOnly !== true,
).map((e) => ({ value: e.value, label: e.label }));

/** `Select` options for the effect picker on a checkbox target (adds Cocher / Décocher). */
const CHECKBOX_CONDITION_EFFECT_OPTIONS: SelectOption[] = CONDITION_EFFECTS.map(
  (e) => ({ value: e.value, label: e.label }),
);

/** Narrow a `Select` value (single or multi) back to a plain string. */
function toSelectValue(value: string | string[]): string {
  return typeof value === 'string' ? value : (value[0] ?? '');
}

/** Narrow a `Select` value back to the operator vocabulary (blank when unknown). */
function toConditionOperator(value: string | string[]): ConditionOperator | '' {
  const raw = toSelectValue(value);
  const match = CONDITION_OPERATORS.find((o) => o.value === raw);
  return match?.value ?? '';
}

/** Human label for an operator (falls back to the raw key for an unknown one). */
function describeConditionOperator(operator: ConditionOperator): string {
  return CONDITION_OPERATORS.find((o) => o.value === operator)?.label ?? operator;
}

/** Human label for an effect payload, matched against the presets. */
function describeConditionEffect(effect: RuleFieldCondition['effect']): string {
  const match = CONDITION_EFFECTS.find(
    (e) =>
      e.effect.visible === effect.visible &&
      e.effect.disabled === effect.disabled &&
      e.effect.required === effect.required &&
      e.effect.checked === effect.checked,
  );
  return match?.label ?? CONDITION_UI.customEffect;
}

/** Render a condition's comparison value for the one-line summary. */
function formatConditionValue(value: unknown): string {
  if (Array.isArray(value)) {
    return (value as unknown[]).map((v) => String(v)).join(', ');
  }
  return String(value);
}

/**
 * One-line summary of a staged condition, e.g.
 * `Quand Catégorie est égal à "EXPERT" → Masquer`. The source field is named by
 * its **base label** when it is among the sibling targets, else by its stable
 * key (a condition may point at a field the editor cannot see).
 */
function describeCondition(
  condition: RuleFieldCondition,
  siblingFields: readonly LayoutTarget[],
): string {
  const source =
    siblingFields.find((t) => t.key === condition.when)?.baseLabel ??
    condition.when;
  const value =
    condition.value === undefined
      ? ''
      : ` "${formatConditionValue(condition.value)}"`;
  return [
    CONDITION_UI.summaryPrefix,
    ` ${source} `,
    describeConditionOperator(condition.operator),
    value,
    ` ${CONDITION_UI.summaryArrow} `,
    describeConditionEffect(condition.effect),
  ].join('');
}

// ---------------------------------------------------------------------------
// Reorder arrows (shared by every reorderable row)
// ---------------------------------------------------------------------------

interface ReorderArrowsProps {
  /** Accessible label fragment, e.g. the row's display label. */
  readonly label: string;
  /** `true` when this is the first row (Up disabled). */
  readonly isFirst: boolean;
  /** `true` when this is the last row (Down disabled). */
  readonly isLast: boolean;
  /** Stable test-id prefix, e.g. `field-idSin` or `row-GAR-001`. */
  readonly testIdSuffix: string;
  readonly onUp: () => void;
  readonly onDown: () => void;
}

/**
 * Up / Down icon buttons that move a row by one position. Replaces the prior
 * native drag handle; emits the same `reorder*` effect via the parent group's
 * handler. Disabled at the boundaries so the gesture is unambiguous.
 */
function ReorderArrows({
  label,
  isFirst,
  isLast,
  testIdSuffix,
  onUp,
  onDown,
}: ReorderArrowsProps) {
  return (
    <HStack gap={1} align="center">
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Move ${label} up`}
        disabled={isFirst}
        onClick={onUp}
        data-testid={`layout-move-up-${testIdSuffix}`}
        leftIcon={<Icon icon={ChevronUp} size="xs" />}
      />
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Move ${label} down`}
        disabled={isLast}
        onClick={onDown}
        data-testid={`layout-move-down-${testIdSuffix}`}
        leftIcon={<Icon icon={ChevronDown} size="xs" />}
      />
    </HStack>
  );
}

// ---------------------------------------------------------------------------
// Per-target row (field / column / section)
// ---------------------------------------------------------------------------

interface TargetRowProps {
  readonly target: LayoutTarget;
  readonly effects: readonly RuleEffect[];
  readonly onEdit: (edit: LayoutEdit) => void;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly onMove: (dir: 'up' | 'down') => void;
  /**
   * Every **field** target of the component — the pool a runtime condition's
   * *source* field is picked from (the row's own key is excluded). Passed by
   * `TargetGroup` for the field group only; other kinds have no condition
   * affordance and leave it empty.
   */
  readonly siblingFields?: readonly LayoutTarget[];
}

/**
 * One inline text input with a local draft + editing flag, committing on blur
 * / Enter exactly like the legacy single-label input. Seeded from the
 * effective value; the native `placeholder` is the base default.
 */
interface InlineTextInputProps {
  readonly value: string;
  readonly nativePlaceholder?: string;
  readonly label: string;
  readonly ariaLabel: string;
  readonly testId: string;
  readonly onCommit: (value: string) => void;
}

function InlineTextInput({
  value,
  nativePlaceholder,
  label,
  ariaLabel,
  testId,
  onCommit,
}: InlineTextInputProps) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  const shown = editing ? draft : value;

  const commit = () => {
    setEditing(false);
    if (draft === value) return;
    onCommit(draft);
  };

  return (
    <TextField
      value={shown}
      size="sm"
      label={label}
      placeholder={nativePlaceholder}
      aria-label={ariaLabel}
      data-testid={testId}
      onChange={(e) => {
        setEditing(true);
        setDraft(e.target.value);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-field runtime-condition sub-panel (`setFieldCondition`)
// ---------------------------------------------------------------------------

interface FieldConditionEditorProps {
  readonly target: LayoutTarget;
  /** Conditions already staged on this field, with their removal indices. */
  readonly conditions: ReadonlyArray<{
    readonly index: number;
    readonly condition: RuleFieldCondition;
  }>;
  /** Field targets of the same component (the row's own key is filtered out). */
  readonly siblingFields: readonly LayoutTarget[];
  readonly onEdit: (edit: LayoutEdit) => void;
}

/**
 * The sub-panel behind a field row's "Condition" button: the staged conditions
 * (one summary line + a Trash button each, emitting `removeCondition` with the
 * row's own index) above an add form that stages a `setFieldCondition`.
 *
 * The form is **local** draft state — nothing is emitted until "Ajouter la
 * condition" is clicked, at which point the whole condition lands as one edit
 * and the form resets. The Add button stays disabled until a source, an
 * operator and an effect are chosen, plus a value when the operator needs one.
 */
function FieldConditionEditor({
  target,
  conditions,
  siblingFields,
  onEdit,
}: FieldConditionEditorProps) {
  const [when, setWhen] = useState('');
  const [operator, setOperator] = useState<ConditionOperator | ''>('');
  const [value, setValue] = useState('');
  const [effectKey, setEffectKey] = useState('');

  // A field can only test ANOTHER field of the same form, so its own key is
  // excluded. The stable key is shown next to the label — it is the contract
  // the emitted effect carries.
  const sourceOptions = useMemo<SelectOption[]>(
    () =>
      siblingFields
        .filter((t) => t.key !== target.key)
        .map((t) => ({
          value: t.key,
          label: `${t.baseLabel} (${t.key})`,
        })),
    [siblingFields, target.key],
  );

  // Cocher / Décocher write a boolean, so they are only offered on a checkbox
  // target; every other field keeps the five state presets.
  const effectOptions =
    target.supportsCheckedCondition === true
      ? CHECKBOX_CONDITION_EFFECT_OPTIONS
      : CONDITION_EFFECT_OPTIONS;

  // Before an operator is picked, assume a value IS needed (so the input is
  // visible by default); `isEmpty` / `isNotEmpty` then hide it.
  const needsValue = operator === '' || !VALUELESS_OPERATORS.has(operator);
  const isListValue = operator !== '' && LIST_OPERATORS.has(operator);
  const canAdd =
    when !== '' &&
    operator !== '' &&
    effectKey !== '' &&
    (!needsValue || value.trim() !== '');

  const addCondition = () => {
    const chosen = CONDITION_EFFECTS.find((e) => e.value === effectKey);
    // `canAdd` already asserts a non-blank operator (TS narrows through the
    // aliased condition), so guarding on it is enough to use it below.
    if (!canAdd || chosen === undefined) return;
    const parsed = parseConditionValue(value, operator);
    onEdit({
      kind: 'field',
      op: 'addCondition',
      key: target.key,
      condition: {
        when,
        operator,
        ...(parsed !== undefined ? { value: parsed } : {}),
        effect: chosen.effect,
      },
    });
    setWhen('');
    setOperator('');
    setValue('');
    setEffectKey('');
  };

  return (
    <VStack
      gap={2}
      className="pl-8"
      data-testid={`layout-condition-panel-${target.key}`}
    >
      <Text size="sm" weight="semibold">
        {CONDITION_UI.stagedHeading}
      </Text>
      {conditions.length === 0 ? (
        <Text size="sm" color="muted">
          {CONDITION_UI.stagedEmpty}
        </Text>
      ) : (
        <VStack gap={1}>
          {conditions.map(({ index, condition }) => (
            <HStack
              key={`${String(index)}-${condition.when}-${condition.operator}`}
              gap={2}
              align="center"
              className="rounded-md border border-secondary-200 p-2"
            >
              <Text size="sm">
                {describeCondition(condition, siblingFields)}
              </Text>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove condition ${String(index)} for ${target.key}`}
                data-testid={`layout-remove-condition-${target.key}-${String(index)}`}
                leftIcon={<Icon icon={Trash2} size="xs" />}
                onClick={() => {
                  onEdit({
                    kind: 'field',
                    op: 'removeCondition',
                    key: target.key,
                    index,
                  });
                }}
              />
            </HStack>
          ))}
        </VStack>
      )}

      <Text size="sm" weight="semibold">
        {CONDITION_UI.addHeading}
      </Text>
      {sourceOptions.length === 0 ? (
        <Text size="sm" color="muted">
          {CONDITION_UI.noSource}
        </Text>
      ) : (
        <VStack gap={2}>
          <div data-testid={`layout-condition-when-${target.key}`}>
            <Select
              options={sourceOptions}
              value={when}
              onChange={(v) => {
                setWhen(toSelectValue(v));
              }}
              label={CONDITION_UI.sourceLabel}
              placeholder={CONDITION_UI.sourcePlaceholder}
              size="sm"
            />
          </div>
          <div data-testid={`layout-condition-operator-${target.key}`}>
            <Select
              options={CONDITION_OPERATOR_OPTIONS}
              value={operator}
              onChange={(v) => {
                setOperator(toConditionOperator(v));
              }}
              label={CONDITION_UI.operatorLabel}
              placeholder={CONDITION_UI.operatorPlaceholder}
              size="sm"
            />
          </div>
          {needsValue && (
            <TextField
              value={value}
              size="sm"
              label={CONDITION_UI.valueLabel}
              placeholder={CONDITION_UI.valuePlaceholder}
              helperText={isListValue ? CONDITION_UI.valueListHelper : undefined}
              aria-label={`Condition value for ${target.key}`}
              data-testid={`layout-condition-value-${target.key}`}
              onChange={(e) => {
                setValue(e.target.value);
              }}
            />
          )}
          <div data-testid={`layout-condition-effect-${target.key}`}>
            <Select
              options={effectOptions}
              value={effectKey}
              onChange={(v) => {
                setEffectKey(toSelectValue(v));
              }}
              label={CONDITION_UI.effectLabel}
              placeholder={CONDITION_UI.effectPlaceholder}
              size="sm"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canAdd}
            data-testid={`layout-add-condition-${target.key}`}
            onClick={addCondition}
          >
            {CONDITION_UI.addButton}
          </Button>
        </VStack>
      )}
    </VStack>
  );
}

/**
 * Sections only support reorder (the effect union has no rename / hide /
 * tooltip for them), so their row is just the key + reorder arrows. Fields,
 * columns and filters get the affordance set their config allows (label /
 * placeholder / helper / tooltip / visibility — gated by the per-target
 * `supports*` flags). Filters mirror columns (rename / hide / reorder) plus a
 * placeholder, but carry no tooltip/helper.
 */
function TargetRow({
  target,
  effects,
  onEdit,
  isFirst,
  isLast,
  onMove,
  siblingFields = [],
}: TargetRowProps) {
  const label = effectiveLabel(target, effects);
  const hidden = isHidden(target, effects);
  const tooltip = effectiveTooltip(target, effects);
  const placeholder = effectivePlaceholder(target, effects);
  const helperText = effectiveHelperText(target, effects);
  // Front-end-required state (add-only): the staged `requireField` or the
  // field's non-required baseline. The control is gated on `supportsRequired`.
  const { required, message: requiredMessage } = effectiveRequired(
    target,
    effects,
  );

  // Runtime form-value conditions staged on this field (append-only list).
  const conditions = effectiveConditions(target, effects);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [conditionOpen, setConditionOpen] = useState(false);
  const supportsRichEdit = target.kind !== 'section';
  // Runtime conditions branch on the FORM's own values, so they only make
  // sense on EntityForm fields (columns / filters / the search box have none).
  const supportsConditionEdit = target.kind === 'field';
  // Tooltips are a field/column/item affordance; filters carry none, so gate the
  // tooltip button + editor on the per-target `supportsTooltip` flag.
  const supportsTooltipEdit = target.supportsTooltip === true;
  // The front-end-required toggle is add-only and offered only for fields that
  // are NOT baseline-required (`supportsRequired === true`).
  const supportsRequiredEdit = target.supportsRequired === true;
  // The kind a rename / hide / reorder edit is emitted under. Filters and
  // columns each map to their own arm; everything else folds into the field
  // arms (info items reuse the `'field'` kind).
  const editKind: 'field' | 'column' | 'filter' =
    target.kind === 'column'
      ? 'column'
      : target.kind === 'filter'
        ? 'filter'
        : 'field';
  // Placeholder edits are emitted under the matching arm (filter placeholders
  // fold into `setFilterPlaceholder`, field placeholders into the field arm).
  const placeholderKind: 'field' | 'filter' =
    target.kind === 'filter' ? 'filter' : 'field';

  const commitRename = (value: string) => {
    onEdit({
      kind: editKind,
      op: 'rename',
      key: target.key,
      label: value,
    } as LayoutEdit);
  };

  const commitPlaceholder = (value: string) => {
    onEdit({
      kind: placeholderKind,
      op: 'placeholder',
      key: target.key,
      placeholder: value,
    } as LayoutEdit);
  };

  const commitHelperText = (value: string) => {
    onEdit({
      kind: 'field',
      op: 'helperText',
      key: target.key,
      helperText: value,
    });
  };

  const commitTooltip = (value: string) => {
    onEdit({
      kind: editKind,
      op: 'tooltip',
      key: target.key,
      tooltip: value,
    } as LayoutEdit);
  };

  const removeTooltip = () => {
    onEdit(
      editKind === 'column'
        ? { kind: 'column', op: 'clearTooltip', key: target.key }
        : { kind: 'field', op: 'clearTooltip', key: target.key },
    );
  };

  const toggleVisibility = () => {
    onEdit({
      kind: editKind,
      op: 'hide',
      key: target.key,
      hidden: !hidden,
      baseHidden: target.baseHidden ?? false,
    } as LayoutEdit);
  };

  // Add-only front-end-required toggle. Turning ON stages a `requireField`
  // carrying the current (or default) message; turning OFF removes the staged
  // require (handled by the reducer's removal slot).
  const commitRequired = (next: boolean) => {
    onEdit({
      kind: 'field',
      op: 'require',
      key: target.key,
      required: next,
      ...(next
        ? {
            message:
              requiredMessage !== undefined && requiredMessage !== ''
                ? requiredMessage
                : DEFAULT_REQUIRED_MESSAGE,
          }
        : {}),
    });
  };

  const commitRequiredMessage = (value: string) => {
    onEdit({
      kind: 'field',
      op: 'require',
      key: target.key,
      required: true,
      message: value,
    });
  };

  return (
    <VStack
      gap={2}
      className={[
        'rounded-md border p-2 border-secondary-200',
        hidden ? 'opacity-60' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <HStack gap={2} align="center">
        <ReorderArrows
          label={target.key}
          isFirst={isFirst}
          isLast={isLast}
          testIdSuffix={`${target.kind}-${target.key}`}
          onUp={() => {
            onMove('up');
          }}
          onDown={() => {
            onMove('down');
          }}
        />
        <Badge variant="default" size="sm">
          {target.key}
        </Badge>

        {supportsRichEdit ? (
          <>
            <Switch
              checked={!hidden}
              onChange={toggleVisibility}
              aria-label={`Toggle visibility of ${target.key}`}
              size="sm"
            />
            <Icon icon={hidden ? EyeOff : Eye} size="sm" aria-hidden />
            {supportsTooltipEdit && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit tooltip for ${target.key}`}
                leftIcon={<Icon icon={MessageSquare} size="xs" />}
                onClick={() => {
                  setTooltipOpen((v) => !v);
                }}
              >
                {tooltip !== '' ? 'Tooltip ✓' : 'Tooltip'}
              </Button>
            )}
            {supportsRequiredEdit && (
              <HStack gap={1} align="center">
                <Switch
                  checked={required}
                  onChange={() => {
                    commitRequired(!required);
                  }}
                  aria-label={`Toggle front-end required for ${target.key}`}
                  data-testid={`layout-required-toggle-${target.key}`}
                  size="sm"
                />
                <Text size="sm" color="muted">
                  Obligatoire (front-end)
                </Text>
              </HStack>
            )}
            {supportsConditionEdit && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit conditions for ${target.key}`}
                data-testid={`layout-condition-toggle-${target.key}`}
                leftIcon={<Icon icon={GitBranch} size="xs" />}
                onClick={() => {
                  setConditionOpen((v) => !v);
                }}
              >
                {conditions.length > 0
                  ? CONDITION_UI.toggleStaged(conditions.length)
                  : CONDITION_UI.toggle}
              </Button>
            )}
          </>
        ) : (
          <Text size="sm" color="muted">
            {target.baseLabel}
          </Text>
        )}
      </HStack>

      {supportsRichEdit && (
        <VStack gap={2} className="pl-8">
          <InlineTextInput
            value={label}
            nativePlaceholder={target.baseLabel}
            label="Label"
            ariaLabel={`Rename ${target.key}`}
            testId={`layout-input-label-${target.key}`}
            onCommit={commitRename}
          />
          {target.supportsPlaceholder === true && (
            <InlineTextInput
              value={placeholder}
              nativePlaceholder={target.basePlaceholder}
              label="Placeholder"
              ariaLabel={`Placeholder for ${target.key}`}
              testId={`layout-input-placeholder-${target.key}`}
              onCommit={commitPlaceholder}
            />
          )}
          {target.supportsHelperText === true && (
            <InlineTextInput
              value={helperText}
              nativePlaceholder={target.baseHelperText}
              label="Helper text"
              ariaLabel={`Helper text for ${target.key}`}
              testId={`layout-input-helper-${target.key}`}
              onCommit={commitHelperText}
            />
          )}
          {supportsRequiredEdit && required && (
            <InlineTextInput
              value={
                requiredMessage !== undefined && requiredMessage !== ''
                  ? requiredMessage
                  : DEFAULT_REQUIRED_MESSAGE
              }
              nativePlaceholder={DEFAULT_REQUIRED_MESSAGE}
              label="Message d'erreur"
              ariaLabel={`Required message for ${target.key}`}
              testId={`layout-input-required-message-${target.key}`}
              onCommit={commitRequiredMessage}
            />
          )}
        </VStack>
      )}

      {supportsRichEdit && supportsTooltipEdit && tooltipOpen && (
        <HStack gap={2} align="center" className="pl-8">
          <TextField
            value={tooltip}
            size="sm"
            placeholder="Tooltip text shown on hover"
            aria-label={`Tooltip text for ${target.key}`}
            onChange={(e) => {
              commitTooltip(e.target.value);
            }}
          />
          {tooltip !== '' && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove tooltip for ${target.key}`}
              data-testid={`layout-remove-tooltip-${target.key}`}
              leftIcon={<Icon icon={Trash2} size="xs" />}
              onClick={removeTooltip}
            >
              Remove tooltip
            </Button>
          )}
        </HStack>
      )}

      {supportsRichEdit && supportsConditionEdit && conditionOpen && (
        <FieldConditionEditor
          target={target}
          conditions={conditions}
          siblingFields={siblingFields}
          onEdit={onEdit}
        />
      )}
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Per-kind group (field / column / section / filter) with arrow reorder
// ---------------------------------------------------------------------------

interface TargetGroupProps {
  readonly kind: LayoutTargetKind;
  readonly targets: readonly LayoutTarget[];
  readonly effects: readonly RuleEffect[];
  readonly onEdit: (edit: LayoutEdit) => void;
}

/**
 * A group of same-kind targets reordered by Up / Down arrow buttons. The
 * displayed order is the **effective** order ({@link effectiveTargetOrder}
 * folds any committed `reorder*` effect), so the editor list re-orders in
 * lock-step with the live preview — and repeated moves stay consistent because
 * each move is computed from the already-resolved order, not the static
 * declaration order. Moving a row swaps it with its neighbour via
 * {@link reorderByArrow} and emits the reorder effect with the full new key
 * order.
 */
function TargetGroup({ kind, targets, effects, onEdit }: TargetGroupProps) {
  // `TargetGroup` is only rendered for the reorderable surfaces.
  const reorderKind = kind as 'field' | 'column' | 'section' | 'filter';
  const orderedTargets = useMemo(
    () => effectiveTargetOrder(targets, reorderKind, effects),
    [targets, reorderKind, effects],
  );

  if (orderedTargets.length === 0) return null;

  const orderedKeys = orderedTargets.map((t) => t.key);

  const move = (key: string, dir: 'up' | 'down') => {
    const order = reorderByArrow(orderedKeys, key, dir);
    onEdit({ kind, op: 'reorder', order } as LayoutEdit);
  };

  return (
    <VStack gap={2}>
      <Text size="sm" weight="semibold">
        {KIND_TITLES[kind]}
      </Text>
      <VStack gap={1}>
        {orderedTargets.map((target, index) => (
          <TargetRow
            key={target.key}
            target={target}
            effects={effects}
            onEdit={onEdit}
            isFirst={index === 0}
            isLast={index === orderedTargets.length - 1}
            onMove={(dir) => {
              move(target.key, dir);
            }}
            // Only the field group can author runtime conditions, and its
            // source pool is the group's own targets (all fields of the
            // component). Other kinds get none.
            siblingFields={kind === 'field' ? targets : undefined}
          />
        ))}
      </VStack>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Title group (single label input)
// ---------------------------------------------------------------------------

interface TitleGroupProps {
  readonly target: LayoutTarget;
  readonly effects: readonly RuleEffect[];
  readonly onEdit: (edit: LayoutEdit) => void;
}

/**
 * One row for the component title: a single label `TextField` seeded from
 * {@link effectiveTitle}, emitting `title/rename` (a blank value reverts to the
 * component default). Rendered only when a `title` target exists.
 */
function TitleGroup({ target, effects, onEdit }: TitleGroupProps) {
  const title = effectiveTitle(target.baseLabel, effects);

  return (
    <VStack gap={2}>
      <Text size="sm" weight="semibold">
        {KIND_TITLES.title}
      </Text>
      <VStack gap={1}>
        <VStack gap={2} className="rounded-md border border-secondary-200 p-2">
          <InlineTextInput
            value={title}
            nativePlaceholder={target.baseLabel}
            label="Title"
            ariaLabel="Edit title"
            testId="layout-input-title"
            onCommit={(value) => {
              onEdit({ kind: 'title', op: 'rename', title: value });
            }}
          />
        </VStack>
      </VStack>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Buttons group (one row per action target)
// ---------------------------------------------------------------------------

interface ActionGroupProps {
  readonly targets: readonly LayoutTarget[];
  readonly effects: readonly RuleEffect[];
  readonly onEdit: (edit: LayoutEdit) => void;
}

/**
 * One row per action target: a label `TextField` (`action/rename`) and a
 * visibility `Switch` (`action/hide`). The switch reflects the draft hide
 * state folded over the target's `baseHidden`.
 */
function ActionGroup({ targets, effects, onEdit }: ActionGroupProps) {
  if (targets.length === 0) return null;

  return (
    <VStack gap={2}>
      <Text size="sm" weight="semibold">
        {KIND_TITLES.action}
      </Text>
      <VStack gap={1}>
        {targets.map((target) => {
          // The switch reflects the effective visibility: a draft `hideAction`
          // forces hidden; otherwise fall back to the base visibility.
          const effectiveHidden =
            isActionHidden(target.key, effects) || (target.baseHidden ?? false);
          const label = effectiveActionLabel(target.key, target, effects);
          return (
            <VStack
              key={target.key}
              gap={2}
              className={[
                'rounded-md border border-secondary-200 p-2',
                effectiveHidden ? 'opacity-60' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <HStack gap={2} align="center">
                <Badge variant="default" size="sm">
                  {target.key}
                </Badge>
                <Switch
                  checked={!effectiveHidden}
                  onChange={() => {
                    onEdit({
                      kind: 'action',
                      op: 'hide',
                      key: target.key,
                      hidden: !effectiveHidden,
                    });
                  }}
                  aria-label={`Toggle visibility of ${target.key}`}
                  size="sm"
                />
                <Icon icon={effectiveHidden ? EyeOff : Eye} size="sm" aria-hidden />
              </HStack>
              <VStack gap={2} className="pl-2">
                <InlineTextInput
                  value={label}
                  nativePlaceholder={target.baseLabel}
                  label="Label"
                  ariaLabel={`Rename ${target.key}`}
                  testId={`layout-input-action-${target.key}`}
                  onCommit={(value) => {
                    onEdit({
                      kind: 'action',
                      op: 'rename',
                      key: target.key,
                      label: value,
                    });
                  }}
                />
              </VStack>
            </VStack>
          );
        })}
      </VStack>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Search group (the single DataList toolbar search box)
// ---------------------------------------------------------------------------

interface SearchGroupProps {
  readonly target: LayoutTarget;
  readonly effects: readonly RuleEffect[];
  readonly onEdit: (edit: LayoutEdit) => void;
}

/**
 * The single toolbar search box: a placeholder `TextField`
 * (`search/placeholder`) and a visibility `Switch` (`search/hide`). The search
 * box has no display label (so no rename input) and there is exactly one per
 * surface (so no reorder). The switch reflects the effective visibility folded
 * over the target's `baseHidden` via {@link isSearchHidden}; toggling emits a
 * `search/hide` edit carrying the base visibility so the reducer can pick the
 * minimal `hideSearch` / `showSearch` (or remove the slot when back to base).
 */
function SearchGroup({ target, effects, onEdit }: SearchGroupProps) {
  const hidden = isSearchHidden(target.baseHidden ?? false, effects);
  const placeholder = effectivePlaceholder(target, effects);

  return (
    <VStack gap={2}>
      <Text size="sm" weight="semibold">
        {KIND_TITLES.search}
      </Text>
      <VStack gap={1}>
        <VStack
          gap={2}
          className={[
            'rounded-md border border-secondary-200 p-2',
            hidden ? 'opacity-60' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <HStack gap={2} align="center">
            <Badge variant="default" size="sm">
              {target.key}
            </Badge>
            <Switch
              checked={!hidden}
              onChange={() => {
                onEdit({
                  kind: 'search',
                  op: 'hide',
                  hidden: !hidden,
                  baseHidden: target.baseHidden ?? false,
                });
              }}
              aria-label="Toggle visibility of search"
              size="sm"
            />
            <Icon icon={hidden ? EyeOff : Eye} size="sm" aria-hidden />
          </HStack>
          <VStack gap={2} className="pl-2">
            <InlineTextInput
              value={placeholder}
              nativePlaceholder={target.basePlaceholder}
              label="Placeholder"
              ariaLabel="Placeholder for search"
              testId="layout-input-search-placeholder"
              onCommit={(value) => {
                onEdit({ kind: 'search', op: 'placeholder', placeholder: value });
              }}
            />
          </VStack>
        </VStack>
      </VStack>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Row-reorder group (data rows — Tier 2 manual order) with arrow reorder
// ---------------------------------------------------------------------------

interface RowReorderGroupProps {
  readonly rows: readonly RowTarget[];
  readonly effects: readonly RuleEffect[];
  readonly support: RowReorderSupport;
  readonly onEdit: (edit: LayoutEdit) => void;
}

/**
 * Arrow-reorder list for **data rows**. Mirrors the field/column/section
 * {@link TargetGroup} mechanic but emits a `reorderRows` edit carrying the
 * rows' **stable ids** in the new order. The displayed order is the *effective*
 * order ({@link effectiveRowOrder} folds any draft `reorderRows` effect), so a
 * committed reorder reflects live.
 *
 * Gated by `support`: when the surface can't manually order rows (server
 * paginated, no stable id, not a DataList) it renders the documented disabled
 * message instead of reorderable rows — the same constraint the engine enforces.
 */
function RowReorderGroup({
  rows,
  effects,
  support,
  onEdit,
}: RowReorderGroupProps) {
  const orderedRows = useMemo(
    () => effectiveRowOrder(rows, effects),
    [rows, effects],
  );

  if (!support.supported) {
    return (
      <VStack gap={2}>
        <Text size="sm" weight="semibold">
          Rows
        </Text>
        <Text size="sm" color="muted" data-testid="row-reorder-disabled">
          {support.reason}
        </Text>
      </VStack>
    );
  }

  if (orderedRows.length === 0) return null;

  const orderedIds = orderedRows.map((r) => r.id);

  const move = (id: string, dir: 'up' | 'down') => {
    const order = reorderByArrow(orderedIds, id, dir);
    onEdit({ kind: 'row', op: 'reorder', order });
  };

  return (
    <VStack gap={2} data-testid="row-reorder-group">
      <Text size="sm" weight="semibold">
        Rows
      </Text>
      <VStack gap={1}>
        {orderedRows.map((row, index) => (
          <HStack
            key={row.id}
            gap={2}
            align="center"
            className="rounded-md border border-secondary-200 p-2"
          >
            <ReorderArrows
              label={`row ${row.id}`}
              isFirst={index === 0}
              isLast={index === orderedRows.length - 1}
              testIdSuffix={`row-${row.id}`}
              onUp={() => {
                move(row.id, 'up');
              }}
              onDown={() => {
                move(row.id, 'down');
              }}
            />
            <Badge variant="default" size="sm">
              {row.id}
            </Badge>
            <Text size="sm">{row.label}</Text>
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/**
 * The full authoring panel. Splits the discovered targets into Title, Fields,
 * Sections, Columns, Filtres (toolbar filters), Recherche (the search box) and
 * Buttons groups and renders each as a reorderable / editable list of
 * affordance rows. When nothing was discovered, shows a hint that the admin can
 * open the page from a live render to populate targets.
 */
export function LayoutAuthoringPanel({
  targets,
  effects,
  onEdit,
  rowTargets = [],
  rowReorderSupport = {
    supported: false,
    reason:
      'Manual row ordering is unavailable for this surface (it requires a list over fully-loaded inline data with a stable row id).',
  },
}: LayoutAuthoringPanelProps) {
  const fields = useMemo(() => targetsOfKind(targets, 'field'), [targets]);
  const columns = useMemo(() => targetsOfKind(targets, 'column'), [targets]);
  const sections = useMemo(() => targetsOfKind(targets, 'section'), [targets]);
  const actions = useMemo(() => targetsOfKind(targets, 'action'), [targets]);
  const filters = useMemo(() => targetsOfKind(targets, 'filter'), [targets]);
  const titleTarget = useMemo(
    () =>
      targets.find(
        (t) => t.kind === 'title' && t.key === TITLE_TARGET_KEY,
      ) ??
      targets.find((t) => t.kind === 'title'),
    [targets],
  );
  const searchTarget = useMemo(
    () =>
      targets.find(
        (t) => t.kind === 'search' && t.key === SEARCH_TARGET_KEY,
      ) ?? targets.find((t) => t.kind === 'search'),
    [targets],
  );

  // The row section appears whenever the surface supports manual ordering (so
  // the admin sees the reorderable rows) OR the support is explicitly disabled
  // (so the documented message is shown). When support defaults to disabled and
  // there are no rows, it still renders the disabled message — but only if some
  // other target exists, so a non-DataList component isn't cluttered.
  const hasRowSection =
    (rowReorderSupport.supported && rowTargets.length > 0) ||
    (!rowReorderSupport.supported && (targets.length > 0 || rowTargets.length > 0));

  if (targets.length === 0 && !hasRowSection) {
    return (
      <Text size="sm" color="muted">
        No editable fields, columns, sections, or rows were detected in the live
        preview. Open this page via CMD+click from a page that renders the
        component so its layout can be captured.
      </Text>
    );
  }

  return (
    <VStack gap={4} data-testid="layout-authoring-panel">
      {titleTarget !== undefined && (
        <TitleGroup target={titleTarget} effects={effects} onEdit={onEdit} />
      )}
      <TargetGroup
        kind="field"
        targets={fields}
        effects={effects}
        onEdit={onEdit}
      />
      <TargetGroup
        kind="section"
        targets={sections}
        effects={effects}
        onEdit={onEdit}
      />
      <TargetGroup
        kind="column"
        targets={columns}
        effects={effects}
        onEdit={onEdit}
      />
      <TargetGroup
        kind="filter"
        targets={filters}
        effects={effects}
        onEdit={onEdit}
      />
      {searchTarget !== undefined && (
        <SearchGroup target={searchTarget} effects={effects} onEdit={onEdit} />
      )}
      <ActionGroup targets={actions} effects={effects} onEdit={onEdit} />
      {hasRowSection && (
        <RowReorderGroup
          rows={rowTargets}
          effects={effects}
          support={rowReorderSupport}
          onEdit={onEdit}
        />
      )}
    </VStack>
  );
}

export default LayoutAuthoringPanel;
